const TOKENIZE_CONFIG_ENDPOINT = "/payment/fatzebra/tokenize-config";
const TOKENIZE_VERIFY_ENDPOINT = "/payment/fatzebra/tokenize-verify";

const buildCallbackUrl = () => {
  if (typeof window === "undefined") {
    throw new Error("Fat Zebra tokenisation requires a browser environment.");
  }

  return `${window.location.origin}/fatzebra-tokenize-callback.html`;
};

const normalizeExpiryYear = (expiryYear) => {
  const year = String(expiryYear ?? "").trim();
  return year.length === 2 ? `20${year}` : year;
};

export const buildDirectPostFields = ({ verification, returnPath, cardDetails }) => ({
  card_holder: cardDetails.cardHolder,
  card_number: cardDetails.cardNumber,
  expiry_month: String(cardDetails.expiryMonth ?? ""),
  expiry_year: String(normalizeExpiryYear(cardDetails.expiryYear)),
  cvv: cardDetails.cvv,
  return_path: returnPath,
  verification,
});

export const buildJsonpRequestUrl = ({ actionUrl, fields, callbackName }) => {
  const params = new URLSearchParams({
    ...fields,
    callback: callbackName,
  });

  return `${actionUrl}?${params.toString()}`;
};

const requestTokenizeConfig = async ({ formEndpoint, returnPath }) => {
  const response = await fetch(`${formEndpoint}${TOKENIZE_CONFIG_ENDPOINT}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ return_path: returnPath }),
  });

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload?.message ?? "Unable to start Fat Zebra tokenisation.");
  }

  return payload;
};

const verifyTokenizeResponse = async ({ formEndpoint, payload }) => {
  const response = await fetch(`${formEndpoint}${TOKENIZE_VERIFY_ENDPOINT}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();
  if (!response.ok || !result?.successful || !result?.token) {
    throw new Error(result?.message ?? "Fat Zebra tokenisation failed.");
  }

  return result;
};

const createJsonpScript = (src) => {
  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  script.type = "text/javascript";
  document.body.appendChild(script);
  return script;
};

const extractJsonpErrorMessage = (payload) => {
  if (Array.isArray(payload?.["errors[]"]) && payload["errors[]"].length > 0) {
    return payload["errors[]"].join(", ");
  }

  if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
    return payload.errors.join(", ");
  }

  if (payload?.message) {
    return payload.message;
  }

  return "Fat Zebra tokenisation failed.";
};

export const tokenizeFatZebraCard = async ({ formEndpoint, cardDetails }) => {
  if (!cardDetails?.cardNumber || !cardDetails?.cvv || !cardDetails?.cardHolder || !cardDetails?.expiryMonth || !cardDetails?.expiryYear) {
    throw new Error("Credit card details are incomplete.");
  }

  const returnPath = buildCallbackUrl();
  const config = await requestTokenizeConfig({ formEndpoint, returnPath });

  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;
    let script = null;
    const callbackName = `starlightFatZebraTokenize${Date.now()}`;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      if (script?.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (typeof window !== "undefined" && window[callbackName]) {
        delete window[callbackName];
      }
    };

    const finish = (fn, value) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      fn(value);
    };

    window[callbackName] = async (payload) => {
      if (String(payload?.r) !== "1" || !payload?.token || !payload?.v) {
        finish(reject, new Error(extractJsonpErrorMessage(payload)));
        return;
      }

      try {
        const verified = await verifyTokenizeResponse({
          formEndpoint,
          payload,
        });

        finish(resolve, {
          cardToken: verified.token,
          cardLast4: String(cardDetails.cardNumber).slice(-4),
          cardType: cardDetails.cardType,
          nameOnCard: cardDetails.cardHolder,
          expiryMonth: String(cardDetails.expiryMonth),
          expiryYear: normalizeExpiryYear(cardDetails.expiryYear),
        });
      } catch (error) {
        finish(reject, error);
      }
    };

    timeoutId = window.setTimeout(() => {
      finish(reject, new Error("Fat Zebra tokenisation timed out."));
    }, 30000);

    const requestUrl = buildJsonpRequestUrl({
      actionUrl: config.action_url,
      fields: buildDirectPostFields({
        verification: config.verification,
        returnPath: config.return_path,
        cardDetails,
      }),
      callbackName,
    });

    script = createJsonpScript(requestUrl);
    script.onerror = () => {
      finish(reject, new Error("Unable to reach Fat Zebra tokenisation."));
    };
  });
};
